// Orchestrator for /claim — the page EVERY new Expy account lands on. Patients,
// providers and office admins all arrive here from a one-time magic link; the API's
// claim endpoint is deliberately kind-agnostic, so one page serves them all (decision: John, 2026-08-01 —
// no separate app.expyhealth.com; this rides on the existing start.expyhealth.com).
//
//   ?token=…  → GET validate → set-password form → success
//   dead link → used / expired / invalid, each with its own honest next step
//
// The invalid + missing cases render IDENTICAL copy on purpose: the page must never
// reveal whether a given token exists.
import { useEffect, useRef, useState } from 'react';
import { validateInvite, claimInvite, type InviteInvalidReason } from '../lib/claimApi';
import { ApiError, RateLimited } from '../lib/api';
import { track } from '../lib/analytics';
import { ClaimShell } from './ClaimShell';
import { SetPasswordForm } from './SetPasswordForm';
import {
  ClaimSuccess, ClaimNotice, adminSignInUrl, claimBadge, claimKindFrom, isStaff, type ClaimKind,
} from './ClaimSuccess';

type Phase = 'loading' | 'form' | 'done' | InviteInvalidReason | 'error';

export function ClaimApp() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  // See ClaimSuccess: the API response can't tell us the account kind, so the link
  // carries it. Absent → patient (the overwhelmingly common case).
  const kind = claimKindFrom(params.get('kind'));

  const [phase, setPhase] = useState<Phase>('loading');
  const [firstName, setFirstName] = useState<string | undefined>();
  const [expiresAt, setExpiresAt] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const headingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Claim your account · Expy Health';
  }, []);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    let cancelled = false;
    validateInvite(token)
      .then((res) => {
        if (cancelled) return;
        if (res.valid) {
          setFirstName(res.firstName);
          setExpiresAt(res.expiresAt);
          setPhase('form');
        } else {
          setPhase(res.reason === 'missing' ? 'invalid' : res.reason || 'invalid');
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Move focus to the new heading whenever we leave the form, so a screen-reader user
  // isn't left on a control that no longer exists.
  useEffect(() => {
    if (phase !== 'loading' && phase !== 'form') headingRef.current?.focus?.();
  }, [phase]);

  async function submit(password: string) {
    setBusy(true);
    setServerError(null);
    try {
      const res = await claimInvite({ token, password });
      setEmail(res.email);
      setPhase('done');
      // The ONLY analytics on this page. No PHI, no PII — a kind marker at most.
      track('claim_completed', { variant: kind });
    } catch (e) {
      if (e instanceof RateLimited) {
        setServerError('Too many attempts from this connection. Give it a few minutes and try again.');
      } else if (e instanceof ApiError && e.status === 410) {
        // Used or expired — ask the server which, so we can show the right screen.
        const reason = await validateInvite(token)
          .then((r) => r.reason)
          .catch(() => undefined);
        setPhase(reason === 'expired' ? 'expired' : 'used');
      } else if (e instanceof ApiError && e.status === 400) {
        // Carries the Cognito password-policy failure verbatim. Surface it.
        setServerError(
          e.message.startsWith('http_') ? "That password wasn't accepted. Try a different one." : e.message,
        );
      } else if (e instanceof ApiError && e.status === 0) {
        setServerError('Connection issue. Check your network and try again.');
      } else {
        setServerError('Something went wrong setting your password. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return (
      <ClaimShell badge={claimBadge(kind)}>
        <div role="status" style={{ fontSize: 16, fontWeight: 700, color: 'var(--slate)' }}>
          Checking your link…
        </div>
      </ClaimShell>
    );
  }

  if (phase === 'form') {
    return (
      <SetPasswordForm
        firstName={firstName}
        expiresAt={expiresAt}
        kind={kind}
        busy={busy}
        serverError={serverError}
        onSubmit={submit}
      />
    );
  }

  if (phase === 'done') {
    return (
      <div ref={headingRef} tabIndex={-1} style={{ outline: 'none' }}>
        <ClaimSuccess email={email} kind={kind} />
      </div>
    );
  }

  const notice = noticeFor(phase, kind);
  return (
    <div ref={headingRef} tabIndex={-1} style={{ outline: 'none' }}>
      <ClaimNotice kind={kind} title={notice.title} body={notice.body} action={notice.action} />
    </div>
  );
}

function noticeFor(phase: Phase, kind: ClaimKind) {
  const signIn = (
    <a
      href={adminSignInUrl}
      style={{
        display: 'inline-block',
        background: 'var(--teal)',
        color: '#fff',
        fontSize: 16,
        fontWeight: 800,
        padding: '15px 30px',
        borderRadius: 10,
        boxShadow: 'var(--teal-shadow)',
      }}
    >
      Go to sign in
    </a>
  );

  if (phase === 'used') {
    const usedBody: Record<ClaimKind, string> = {
      provider:
        'Your password is already set. Sign in to your provider account with the email we invited and the password you chose.',
      admin: 'Your password is already set. Sign in with the email we invited and the password you chose.',
      patient:
        'Your password is already set. Open the Expy app and sign in with your email and the password you chose. If you’ve forgotten it, use “Forgot password” there.',
    };
    return {
      title: 'This link has already been used.',
      body: usedBody[kind],
      action: isStaff(kind) ? signIn : undefined,
    };
  }
  if (phase === 'expired') {
    const expiredBody: Record<ClaimKind, string> = {
      provider: 'Claim links are short-lived for security. Ask Expy to send you a fresh invite and it’ll land in your inbox.',
      admin:
        'Claim links are short-lived for security. Ask whoever added you, or Expy, to send you a fresh invite, and it’ll land in your inbox.',
      patient:
        'Claim links are short-lived for security. Ask your care team or Expy to send you a new one, and it’ll land in your inbox.',
    };
    return {
      title: 'This link has expired.',
      body: expiredBody[kind],
      action: undefined,
    };
  }
  if (phase === 'error') {
    return {
      title: 'We couldn’t check your link.',
      body: 'Something went wrong on our side or with your connection. Try again in a minute. The link in your email is still good.',
      action: undefined,
    };
  }
  // 'invalid' and 'missing' share this wording deliberately — no token oracle.
  return {
    title: 'This link isn’t valid.',
    body: 'It may have been trimmed by your email app, or replaced by a newer one. Open the most recent Expy email and use the link there, or ask for a fresh one.',
    action: undefined,
  };
}
